/**
 * Nominal typing for identifiers.
 *
 * Every entity here is keyed by an opaque string id, and this system has a lot
 * of them — a person id, a sub area id, a request id and an area id are all
 * strings and all get passed around together. Branding them means passing a
 * `SubAreaId` where a `PersonId` belongs is a compile error rather than a
 * lookup that silently returns nothing.
 *
 * This also closes a bug class the prototype has by design: it keys people by
 * display name (`leads: ['Ana Cifuentes']`). Two people named the same, or a
 * rename, quietly corrupts the graph. Ids are surrogate and never reused.
 */

declare const brand: unique symbol;

export type Brand<T, B extends string> = T & { readonly [brand]: B };

export type CompanyId = Brand<string, 'CompanyId'>;
export type AreaId = Brand<string, 'AreaId'>;
export type SubAreaId = Brand<string, 'SubAreaId'>;
export type PersonId = Brand<string, 'PersonId'>;
export type RequestId = Brand<string, 'RequestId'>;
export type ApprovalId = Brand<string, 'ApprovalId'>;
export type WorkItemId = Brand<string, 'WorkItemId'>;
export type PermissionRequestId = Brand<string, 'PermissionRequestId'>;
export type IncapacityId = Brand<string, 'IncapacityId'>;
export type EmergencyContactId = Brand<string, 'EmergencyContactId'>;
export type HolidayId = Brand<string, 'HolidayId'>;
export type AuditEntryId = Brand<string, 'AuditEntryId'>;
export type BalanceId = Brand<string, 'BalanceId'>;

/**
 * Casts a raw string to a branded id. Call this only at the system boundary —
 * inside a codec decoding a Firestore document, or in a test fixture. Nothing
 * in the domain or application layer should need it.
 */
export const asId = <B extends string>(raw: string): Brand<string, B> =>
  raw as Brand<string, B>;

/** Strips the brand. Rarely needed — branded ids are already usable as strings. */
export const idValue = (id: Brand<string, string>): string => id;
